import { Globe, Loader2 } from "lucide-react";
import { useState } from "react";
import { useLocaleState, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { siteHref, siteMethod } from "./siteLink";
import { formatDay } from "./projectUtils";
import { usePartner } from "./useInvited";

type Props = {
  companyId: number | string;
  domain?: string | null;
  website?: string | null;
  method?: string | null;
  sourceUrl?: string | null;
  foundAt?: string | null;
  // Appelé après un signalement, pour relire la fiche.
  onReported?: () => void;
  // Sur la carte d'un contact : le lien seul.
  compact?: boolean;
};

/** Site d'une société, avec d'où il vient (règle de traçabilité) et le signalement d'un site faux. */
export const CompanySite = ({
  companyId,
  domain,
  website,
  method,
  sourceUrl,
  foundAt,
  onReported,
  compact = false,
}: Props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const partner = usePartner();
  const href = siteHref(domain, website);
  if (!href || !domain) return null;
  const link = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:no-underline"
    >
      {domain}
    </a>
  );
  if (compact) {
    return (
      <span className="inline-flex items-center gap-1">
        <Globe className="h-3 w-3" />
        {link}
      </span>
    );
  }
  return (
    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1">
      <Globe className="h-3 w-3" />
      {link}
      <span>
        · {translate(`resources.projects.site.found.${siteMethod(method)}`)}
        {foundAt &&
          ` ${translate("resources.projects.site.on", { date: formatDay(foundAt, locale ?? "fr") })}`}
      </span>
      {sourceUrl && (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline"
        >
          · {translate("resources.projects.site.source")}
        </a>
      )}
      {!partner && (
        <ReportSite companyId={companyId} domain={domain} onDone={onReported} />
      )}
    </div>
  );
};

// Signalement d'un site faux : retiré de la société par la base, qui prévient le responsable des données
// (`report_company_site`). Confirmation dans la ligne, sans boîte de dialogue du navigateur.
const ReportSite = ({
  companyId,
  domain,
  onDone,
}: {
  companyId: number | string;
  domain: string;
  onDone?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    const { error } = await getSupabaseClient().rpc("report_company_site", {
      company: companyId,
      note: note || null,
    });
    setSending(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    setOpen(false);
    notify("resources.projects.site.report.done", { type: "info" });
    onDone?.();
  };
  if (!open) {
    return (
      <button
        type="button"
        className="underline ml-1 hover:text-destructive"
        title={translate("resources.projects.site.report.title")}
        onClick={() => setOpen(true)}
      >
        {translate("resources.projects.site.report.action")}
      </button>
    );
  }
  return (
    <div className="mt-1 mb-2 flex w-full max-w-md flex-col gap-2 border-l-2 border-border pl-3 text-foreground">
      <div className="text-xs font-medium">
        {translate("resources.projects.site.report.title")} : {domain}
      </div>
      <div className="text-xs text-muted-foreground">
        {translate("resources.projects.site.report.help")}
      </div>
      <input
        className="h-8 rounded-md border bg-background px-2 text-xs"
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={translate("resources.projects.site.report.note")}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={sending}
          onClick={send}
        >
          {sending && <Loader2 className="h-3 w-3 animate-spin" />}
          {translate("resources.projects.site.report.confirm")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setOpen(false)}
        >
          {translate("resources.projects.site.report.cancel")}
        </Button>
      </div>
    </div>
  );
};
